import {
  Dispatch, JSX, SetStateAction, useRef, useState,
} from 'react';
import {
  Box,
  Divider,
  Paper,
  styled,
} from '@mui/material';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import { Assay } from '@zero-dash/types';
import AssayMenu from './AssayMenu';
import CustomTypography from '@/components/Common/Typography';
import CustomButton from '@/components/Common/Button';

const CircleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '20px',
  minWidth: '20px',
  padding: '0 4px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '100px',
  border: `1px solid ${theme.colours.core.green150}`,
  backgroundColor: theme.colours.core.green10,
}));

interface IProps {
  selectedBiomaterialIds: number[];
  onClose: () => void;
  onApplyAssays: () => void;
  selectedAssays: Assay[];
  setSelectedAssays: Dispatch<SetStateAction<Assay[]>>;
  canSelectAssays: boolean;
}

export default function BiomaterialSelectionActionBar({
  selectedBiomaterialIds,
  onApplyAssays,
  onClose,
  selectedAssays,
  setSelectedAssays,
  canSelectAssays,
}: IProps): JSX.Element {
  const [anchorElPaper, setAnchorElPaper] = useState<null | HTMLElement>(null);

  const paperRef = useRef<HTMLDivElement | null>(null);

  const handleCloseMenu = (): void => {
    setAnchorElPaper(null);
    setSelectedAssays([]);
  };

  return (
    <Paper
      ref={paperRef}
      elevation={8}
      sx={{ padding: '12px 16px' }}
    >
      <Box display="flex" gap="12px">
        <Box
          display="flex"
          gap="7px"
          alignItems="center"
        >
          <CircleIcon>
            <CustomTypography fontWeight="medium">{selectedBiomaterialIds.length}</CustomTypography>
          </CircleIcon>
          <CustomTypography fontWeight="medium">selected</CustomTypography>
          <XIcon
            width="20px"
            height="20px"
            onClick={onClose}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <Divider
          orientation="vertical"
          flexItem
        />
        <CustomButton
          label={(
            <Box display="flex">
              <CustomTypography>Assign assays</CustomTypography>
              <ChevronDownIcon
                style={{ transform: anchorElPaper ? 'rotate(180deg)' : undefined }}
              />
            </Box>
          )}
          variant="subtle"
          sx={{
            borderRadius: '4px',
            height: '28px',
            padding: '4px 8px',
          }}
          onClick={(): void => {
            setAnchorElPaper(paperRef.current);
          }}
          disabled={!canSelectAssays}
        />
      </Box>
      {anchorElPaper && (
        <AssayMenu
          anchorEl={anchorElPaper}
          handleClose={handleCloseMenu}
          selectedAssays={selectedAssays}
          setSelectedAssays={setSelectedAssays}
          onApplyAssays={onApplyAssays}
        />
      )}
    </Paper>
  );
}
